import { forwardRef, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $themeStore, $profileStore, $profileFeatureStore, $menuStore, $initialAccountsStore } from '@lib/store';
import { useRouteHook, useTabs } from '@shared/hooks';
import { AuthUtils, PaletteUtils, RoutePaths, RouteUtils } from '@shared/utils';
import { Button, ColorsEnum, Text, Icon } from '@shared/widgets';
import LogoLight from '@assets/icons/logo/logo-name-big.svg';
import LogoDark from '@assets/icons/logo/logo-name-big-dark.svg';
import LogoLightSmall from '@assets/icons/logo/logo-small.svg';
import LogoDarkSmall from '@assets/icons/logo/logo-small-dark.svg';
import LogoVcfcLight from '@assets/icons/logo/logo-vcfc-big.svg';
import LogoVcfcDark from '@assets/icons/logo/logo-vcfc-big-dark.svg';
import LogoVcfcLightSmall from '@assets/icons/logo/logo-vcfc-small.svg';
import LogoVcfcDarkSmall from '@assets/icons/logo/logo-vcfc-small-dark.svg';
import ChevronIcon from '@assets/icons/chevron.svg';
import MoonIcon from '@assets/icons/moon.svg';
import LogoutIcon from '@assets/icons/logout.svg';
import UserIcon from '@assets/icons/user.svg';

const getLogo = (isDark: boolean, isCollapsed: boolean) => {
  if (PaletteUtils.isInsigniaPalette) {
    if (!isDark) {
      return isCollapsed ? <LogoLightSmall /> : <LogoLight />;
    } else {
      return isCollapsed ? <LogoDarkSmall /> : <LogoDark />;
    }
  }
  if (PaletteUtils.isVcfcPalette) {
    if (!isDark) {
      return isCollapsed ? <LogoVcfcLightSmall /> : <LogoVcfcLight />;
    } else {
      return isCollapsed ? <LogoVcfcDarkSmall /> : <LogoVcfcDark />;
    }
  }
  return null;
};

export const SideBar = forwardRef<HTMLDivElement>((_, ref) => {
  const location = useLocation();
  const { navigate } = useRouteHook();

  const profileStore = useUnit($profileStore.store);
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const isCollapsedMenu = useUnit($menuStore.store);
  const isDarkTheme = useUnit($themeStore.store);
  const accountStore = useUnit($initialAccountsStore.store);

  const tabsState = useTabs();
  useEffect(() => {
    if (tabsState.loading) return;
    const isOnPaymentsRoute = location.pathname.startsWith('/payments');
    if (isOnPaymentsRoute && !tabsState.hasPayment) navigate(RoutePaths.dashboard.onNavigate());
  }, [location.pathname, tabsState.loading, tabsState.hasPayment]);

  const allowedMenuItems = useMemo(
    () => RouteUtils.getAllowedMenuItems({ profile: profileStore.data, features: profileFeatureStore.data, accounts: accountStore.data }),
    [profileStore.data, profileFeatureStore.data, accountStore.data],
  );
  return (
    <div ref={ref} className='w-auto h-screen flex flex-col bg-white dark:bg-soft-black border-r border-r-gray-black dark:border-r-gray-wet-asphalt'>
      <div className='min-h-[70px] h-[70px] max-h-[70px]'>
        <div className={clsx('h-full flex py-4 justify-between items-center', isCollapsedMenu ? 'px-6' : 'px-8')}>
          <NavLink to='/' className='flex h-full justify-center items-center'>
            {getLogo(isDarkTheme, isCollapsedMenu)}
          </NavLink>
          {!isCollapsedMenu && (
            <Button color='primary' className='w-8 h-8 !p-0 rounded-sm' onClick={() => $menuStore.events.setIsCollapsed(!isCollapsedMenu)}>
              <Icon icon={<ChevronIcon />} color={PaletteUtils.getColor('secondary', 'white')} />
            </Button>
          )}
        </div>
      </div>
      <div className='flex flex-col overflow-auto py-6 border-t border-t-gray-black dark:border-t-gray-wet-asphalt no-scrollbar'>
        {allowedMenuItems.map((item, idx) => {
          const isActive = location.pathname === item.link || location.pathname.startsWith(item.link + '/');

          return (
            <NavLink
              key={idx}
              to={item.link}
              className={clsx(
                'flex w-full px-8 py-3 cursor-pointer mb-4',
                isActive && [
                  'border-r-2 border-r-primary',
                  PaletteUtils.isInsigniaPalette && 'bg-yellow-brown',
                  PaletteUtils.isVcfcPalette && 'bg-primary-transparent dark:bg-slate',
                ],
              )}
            >
              <Icon icon={item.Icon} className={clsx(isActive && ['!text-primary', PaletteUtils.isVcfcPalette && 'dark:!text-white'])} />
              {!isCollapsedMenu && <Text className='ml-3 my-auto'>{item.name}</Text>}
            </NavLink>
          );
        })}
      </div>
      <div className='mt-auto flex flex-col border-t border-t-gray-black dark:border-t-gray-wet-asphalt'>
        <div className='flex flex-col py-6'>
          <div className='flex w-full px-8 py-3 cursor-pointer' onClick={() => AuthUtils.logOut()}>
            <div className='flex justify-center items-center'>
              <Icon icon={<LogoutIcon />} />
            </div>
            {!isCollapsedMenu && (
              <Text className='ml-3 my-auto whitespace-nowrap' color={ColorsEnum.Secondary}>
                Log out
              </Text>
            )}
          </div>
          <div
            className={clsx('flex w-full px-8 py-3', isCollapsedMenu && 'cursor-pointer')}
            onClick={() => {
              if (!isCollapsedMenu) return;
              $themeStore.events.setIsDarkMode(!isDarkTheme);
            }}
          >
            <div className='flex justify-center items-center'>
              <Icon icon={<MoonIcon />} />
            </div>
            {!isCollapsedMenu && (
              <>
                <Text className='mx-3 my-auto whitespace-nowrap' color={ColorsEnum.Secondary}>
                  Dark mode
                </Text>
                <label className='inline-flex items-center cursor-pointer ml-auto'>
                  <input type='checkbox' checked={isDarkTheme} onChange={() => {}} className='sr-only peer' />
                  <div
                    onClick={() => $themeStore.events.setIsDarkMode(!isDarkTheme)}
                    className="
                      relative w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700
                      peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                      after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-light-telegray after:border
                      after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary bg-very-pale-blue 
                      "
                  ></div>
                </label>
              </>
            )}
          </div>
        </div>
        <NavLink
          to={profileFeatureStore.data?.profileSettings.viewProfile ? '/profile' : ''}
          className={clsx('bg-primary flex py-3 px-6', PaletteUtils.isVcfcPalette && 'dark:bg-slate')}
        >
          <div className='flex rounded-full w-8 h-8 items-center justify-center bg-very-pale-blue'>
            <Icon icon={<UserIcon />} color='black' />
          </div>
          {!isCollapsedMenu && (
            <Text
              className='ml-3 my-auto font-semibold text-white'
              weight='semibold'
              color={PaletteUtils.getColor(ColorsEnum.Black, ColorsEnum.White)}
            >
              {profileStore.data?.userName}
            </Text>
          )}
        </NavLink>
      </div>
    </div>
  );
});
